import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProfilePage.css';
import PaymentModal from './PaymentModal';
import AppDownloadSection from './components/AppDownloadSection';
import { DEFAULT_APP } from './constants/mobileApps';
import { firePAEvent } from './utils/analytics';

// Blob storage URL for profile data
const BLOB_STORAGE_URL = 'https://physioactivityweb.blob.core.windows.net/profiles';

const ProfilePage = () => {
    const { profileSlug } = useParams();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState(6); // Default to 6 months

    // Generate star display from rating
    const generateStars = (rating) => {
        const fullStars = Math.floor(rating);
        return '⭐'.repeat(fullStars);
    };

    // Calculate price based on duration
    const calculatePrice = (basePrice, duration) => {
        const discounts = { 1: 0, 3: 0.10, 6: 0.20, 12: 0.40 };
        const discount = discounts[duration] || 0;
        const total = Math.round(basePrice * duration * (1 - discount));
        const monthly = Math.round(total / duration);
        return { total, monthly };
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            // If no profileSlug, redirect to subscribe page
            if (!profileSlug) {
                navigate('/subscribe', { replace: true });
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // First, try to resolve the slug via PageIndex API
                // This supports: RowKey (code), vanityCode, or fullSlug
                let blobPath;
                try {
                    const resolveResponse = await fetch(`/api/resolve-page?type=profile&slug=${encodeURIComponent(profileSlug)}`);
                    if (resolveResponse.ok) {
                        const resolved = await resolveResponse.json();
                        blobPath = resolved.blobPath;
                        // Redirect to canonical URL if slug doesn't match fullSlug
                        if (resolved.fullSlug && resolved.fullSlug !== profileSlug) {
                            navigate(`/profile/${resolved.fullSlug}`, { replace: true });
                            return;
                        }
                    }
                } catch (resolveErr) {
                    // API not available, continue with fallback
                    console.log('resolve-page API not available, using fallback');
                }

                // Fallback: try direct blob access (original behavior)
                if (!blobPath) {
                    blobPath = `profiles/${profileSlug}/data.json`;
                }

                // Fetch profile data from blob storage
                const dataResponse = await fetch(`https://physioactivityweb.blob.core.windows.net/${blobPath}`);

                if (!dataResponse.ok) {
                    if (dataResponse.status === 404) {
                        setError('Profile not found');
                        setLoading(false);
                        return;
                    }
                    throw new Error('Failed to fetch profile data');
                }

                const data = await dataResponse.json();

                // Matches the gtag call the prerendered static bio pages fire inline
                // (public/profile/*/index.html) — those exist for AI crawlers, which
                // don't run this component at all, so React-rendered profiles need
                // their own call to be counted.
                firePAEvent('profile_view', { profile_slug: profileSlug });

                // Custom-HTML renderer: replace entire document with the blob page,
                // no React chrome. Used for practice-owned marketing pages.
                // Field naming per Kurama PM cross-PO decision 2026-04-15
                // (DEC-20260415-v2-bio-blob-renderer-field-naming).
                if (data.renderer === 'custom-html' && data.customPagePath) {
                    const htmlResponse = await fetch(`https://physioactivityweb.blob.core.windows.net/${data.customPagePath}`);
                    if (htmlResponse.ok) {
                        const htmlText = await htmlResponse.text();
                        document.open();
                        document.write(htmlText);
                        document.close();
                        return;
                    }
                }

                setProfileData(data);

                // Fetch reviews (separate file) - use fullSlug from data
                const reviewsPath = blobPath.replace('/data.json', '/reviews.json');
                try {
                    const reviewsResponse = await fetch(`https://physioactivityweb.blob.core.windows.net/${reviewsPath}`);
                    if (reviewsResponse.ok) {
                        const reviewsData = await reviewsResponse.json();
                        setReviews(reviewsData || []);
                    }
                } catch (reviewErr) {
                    // Reviews are optional, don't fail if missing
                    console.log('No reviews found for profile');
                }

            } catch (err) {
                console.error('Error fetching profile data:', err);
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [profileSlug]);

    const handleOpenPaymentModal = (plan) => {
        // Include duration info with the plan
        const planWithDuration = {
            ...plan,
            selectedDuration,
            ...calculatePrice(plan.price, selectedDuration)
        };
        setSelectedPlan(planWithDuration);
        setIsPaymentModalOpen(true);
    };

    const handleClosePaymentModal = () => {
        setIsPaymentModalOpen(false);
        setSelectedPlan(null);
    };

    if (loading) {
        return (
            <div className="advisor-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="advisor-page">
                <div className="loading-container">
                    <p>{error}</p>
                    {/* Was: Subscribe → /subscribe. Re-pointed 28 Jun 2026 per Website Review S2 — matching happens in the app. */}
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/getstarted')}
                        style={{ marginTop: '1rem' }}
                    >
                        Get the app
                    </button>
                </div>
            </div>
        );
    }

    // Data is already parsed from JSON blob (no need to parse JSON strings)
    const qualifications = profileData?.qualifications || [];
    const conditionsTreated = profileData?.conditionsTreated || [];
    const plans = (profileData?.plans || []).filter(p => p.active !== false);
    const faqs = profileData?.faqs || [];
    const languages = profileData?.languages || ['English'];
    const aggregateRating = profileData?.aggregateRating || (reviews.length > 0
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
        : '5.0');
    const reviewCount = profileData?.reviewCount || reviews.length;

    // Build display name
    const displayName = profileData?.displayName ||
        (profileData?.title
            ? `${profileData.title} ${profileData.firstName} ${profileData.lastName}`
            : `${profileData?.firstName || ''} ${profileData?.lastName || ''}`);

    const displayNameWithCredentials = profileData?.postNominals
        ? `${displayName}, ${profileData.postNominals}`
        : displayName;

    // Get tier icon
    const getTierIcon = (tier) => {
        const icons = { bronze: '🥉', silver: '🥈', gold: '🥇', premium: '⭐' };
        return icons[tier] || '📦';
    };

    return (
        <div className="advisor-page">
            <div className="advisor-container">
                {profileData && (
                    <>
                        {/* Profile Card */}
                        <div className="profile-card">
                                <div className="cover-photo"></div>
                                <div className="profile-header">
                                    {profileData.photoUrl ? (
                                        <img
                                            src={profileData.photoUrl}
                                            alt={profileData.photoAltText || `${displayName}`}
                                            className="profile-photo"
                                        />
                                    ) : (
                                        <div className="profile-photo">👨‍⚕️</div>
                                    )}
                                    <h1 className="profile-name">{displayNameWithCredentials}</h1>
                                    <p className="profile-headline">{profileData.headline}</p>
                                    <p className="profile-location">📍 {profileData.city}, {profileData.country}</p>

                                    {/* Trust Badges */}
                                    <div className="badges">
                                        {profileData.hcpcVerified && (
                                            <span className="badge verified">✓ HCPC Verified</span>
                                        )}
                                        {profileData.cspMember && (
                                            <span className="badge">✓ CSP Member</span>
                                        )}
                                        {profileData.yearsExperience && (
                                            <span className="badge">{profileData.yearsExperience}+ Years Experience</span>
                                        )}
                                    </div>

                                    {/* Social Links */}
                                    {(profileData.websiteUrl || profileData.linkedInUrl) && (
                                        <div className="social-links">
                                            {profileData.websiteUrl && (
                                                <a href={profileData.websiteUrl} className="social-link" target="_blank" rel="noopener noreferrer">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                                    </svg>
                                                    Website
                                                </a>
                                            )}
                                            {profileData.linkedInUrl && (
                                                <a href={profileData.linkedInUrl} className="social-link" target="_blank" rel="noopener noreferrer">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
                                                    </svg>
                                                    LinkedIn
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                        </div>

                        {/* At a Glance (includes Conditions) */}
                        <div className="section" id="quick-facts">
                                <h2 className="section-title">At a Glance</h2>
                                <div className="quick-facts-grid">
                                    {profileData.primarySpecialty && (
                                        <div className="quick-fact">
                                            <span className="fact-label">Specialties</span>
                                            <span className="fact-value">{profileData.primarySpecialty}</span>
                                        </div>
                                    )}
                                    <div className="quick-fact">
                                        <span className="fact-label">Languages</span>
                                        <span className="fact-value">{languages.join(', ')}</span>
                                    </div>
                                    <div className="quick-fact">
                                        <span className="fact-label">Response Time</span>
                                        <span className="fact-value">{profileData.responseTime || 'Within 24 hours'}</span>
                                    </div>
                                </div>
                                {/* Conditions Treated */}
                                {conditionsTreated.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <span className="fact-label" style={{ display: 'block', marginBottom: '0.5rem' }}>Conditions Treated</span>
                                        <div className="conditions-grid">
                                            {conditionsTreated.map((condition, index) => (
                                                <span key={index} className="condition-tag">{condition}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                        </div>

                        {/* About (with mini credentials) */}
                        <div className="section" id="about">
                            <h2 className="section-title">
                                {`About ${profileData.title || ''} ${profileData.lastName || displayName}`.trim()}
                            </h2>
                            <p className="about-text">{profileData.fullBio}</p>

                            {/* Mini Credentials */}
                            {qualifications.length > 0 && (
                                <div className="mini-credentials">
                                    {qualifications.map((qual, index) => (
                                        <span key={index} className="mini-cred">{qual.title}</span>
                                    ))}
                                    {profileData.hcpcVerified && (
                                        <span className="mini-cred">HCPC Registered</span>
                                    )}
                                    {profileData.cspMember && (
                                        <span className="mini-cred">CSP Member</span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* How It Works */}
                        <div className="section" id="how-it-works">
                            <h2 className="section-title">How It Works</h2>
                            <div className="how-it-works-streamlined">
                                <div className="step-item">
                                    <div className="step-number">1</div>
                                    <h3 className="step-title">Choose Your Plan</h3>
                                    <p className="step-description">Select the program that best suits your condition and needs</p>
                                </div>
                                <div className="step-item">
                                    <div className="step-number">2</div>
                                    <h3 className="step-title">Complete Booking</h3>
                                    <p className="step-description">Securely pay and instantly receive your unique access code</p>
                                </div>
                                <div className="step-item">
                                    <div className="step-number">3</div>
                                    <h3 className="step-title">Download the App</h3>
                                    <p className="step-description">Get SteadyStride and complete your health questionnaire</p>
                                </div>
                                <div className="step-item">
                                    <div className="step-number">4</div>
                                    <h3 className="step-title">Enter Your Code</h3>
                                    <p className="step-description">Input your access code to unlock your program</p>
                                </div>
                                <div className="step-item">
                                    <div className="step-number">5</div>
                                    <h3 className="step-title">Begin Treatment</h3>
                                    <p className="step-description">Attend your video consultation and start your program</p>
                                </div>
                            </div>
                        </div>

                        {/* Subscription Plans */}
                        {plans.length > 0 && (
                            <div className="section" id="programs">
                                <h2 className="section-title">Subscription Plans</h2>

                                <div className="voucher-hint">
                                    <p>Have a voucher or access code? You can apply it at checkout for a discount or complimentary access. Codes may be provided by your health professional, employer, or as part of a research study.</p>
                                </div>

                                {/* Duration Toggle */}
                                <div className="duration-toggle">
                                    <div className="duration-toggle-inner">
                                        <button
                                            className={`duration-btn ${selectedDuration === 1 ? 'active' : ''}`}
                                            onClick={() => setSelectedDuration(1)}
                                        >
                                            1 Month
                                        </button>
                                        <button
                                            className={`duration-btn ${selectedDuration === 3 ? 'active' : ''}`}
                                            onClick={() => setSelectedDuration(3)}
                                        >
                                            3 Months
                                            <span className="savings">Save 10%</span>
                                        </button>
                                        <button
                                            className={`duration-btn ${selectedDuration === 6 ? 'active' : ''}`}
                                            onClick={() => setSelectedDuration(6)}
                                        >
                                            6 Months
                                            <span className="savings">Save 20%</span>
                                        </button>
                                        <button
                                            className={`duration-btn ${selectedDuration === 12 ? 'active' : ''}`}
                                            onClick={() => setSelectedDuration(12)}
                                        >
                                            12 Months
                                            <span className="savings">Save 40%</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="programs-grid">
                                    {plans.map((plan, index) => {
                                        const { total, monthly } = calculatePrice(plan.price, selectedDuration);
                                        const tier = plan.tier || plan.id;
                                        return (
                                            <div key={index} className={`program-card ${tier}`}>
                                                {plan.popular && <div className="program-badge popular">MOST POPULAR</div>}
                                                <div className="program-tier">
                                                    <span className="program-tier-icon">{getTierIcon(tier)}</span>
                                                    <span className="program-tier-name">{plan.name}</span>
                                                </div>
                                                <p className="program-subtitle">{plan.subtitle}</p>
                                                <div className="program-price">
                                                    <span className="price-amount">£{total}</span>
                                                    <span className="price-period"> total</span>
                                                    <div className="price-monthly">£{monthly}/month</div>
                                                </div>
                                                <ul className="program-features">
                                                    {plan.features.map((feature, fIndex) => (
                                                        <li key={fIndex}>{feature}</li>
                                                    ))}
                                                </ul>
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => handleOpenPaymentModal(plan)}
                                                >
                                                    Select {plan.name}
                                                </button>
                                            </div>
                                        );
                                    })}

                                </div>
                            </div>
                        )}

                        {/* App Download */}
                        <div className="section">
                            <AppDownloadSection
                                appId={profileData?.mobileApp?.appId || DEFAULT_APP}
                            />
                        </div>

                        {/* Reviews */}
                        {reviews.length > 0 && (
                            <div className="section" id="reviews">
                                <h2 className="section-title">Reviews</h2>
                                <div className="reviews-summary">
                                    <div className="reviews-score">{aggregateRating}</div>
                                    <div className="reviews-meta">
                                        <div className="stars">⭐⭐⭐⭐⭐</div>
                                        <div className="count">Based on {reviewCount} reviews</div>
                                    </div>
                                </div>
                                <div className="testimonials-grid">
                                    {reviews.slice(0, 3).map((review, index) => (
                                        <div key={index} className="testimonial-card">
                                            <div className="testimonial-stars">{generateStars(review.rating)}</div>
                                            <p className="testimonial-text">"{review.reviewText}"</p>
                                            <div className="testimonial-author">
                                                <strong>{review.reviewerName}</strong>
                                                <span>{review.conditionTreated}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* FAQs */}
                        {faqs.length > 0 && (
                            <div className="section" id="faq">
                                <h2 className="section-title">Frequently Asked Questions</h2>
                                {faqs.map((faq, index) => (
                                    <div key={index} className="faq-item">
                                        <h3 className="faq-question">{faq.question}</h3>
                                        <p className="faq-answer">{faq.answer}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Disclaimer */}
                        <div className="section disclaimer-section-advisor">
                            <h3 className="disclaimer-title">Disclaimer</h3>
                            <p className="disclaimer-text">This platform provides supplementary guidance and is not a replacement for seeking care through traditional healthcare pathways such as the NHS, your GP, or emergency services. If you are experiencing serious pain, injury, or medical concerns, please contact your healthcare provider or visit your local A&E department. In case of emergency, always call 999.</p>
                        </div>
                    </>
                )}
            </div>

            <PaymentModal
                isOpen={isPaymentModalOpen}
                onClose={handleClosePaymentModal}
                plan={selectedPlan}
                profileSlug={profileSlug}
                profileData={profileData}
            />
        </div>
    );
};

export default ProfilePage;
